/**
 * SP6 · Globe / RegionMap / CountryMap shared SVG primitives.
 *
 * These render the three on-map incident glyphs from the SOC threat-map spec:
 *   - `IncidentDot`        → 8px solid dot + 25% halo, severity-coloured
 *   - `IncidentPulseRing`  → one-shot 1.5s scale-out ring (new incidents)
 *   - `UnattribBurst`      → radial burst + UNATTRIB label (no-origin attacks)
 *
 * Colours come from `--severity-{critical|high|medium|low}` CSS custom
 * properties; nothing in this module hardcodes a hex or rgb value. Consumers
 * supply the severity and the primitive resolves to the right token.
 *
 * Scope: SVG primitives only — no positioning, no d3 projection calls. The
 * Globe/Region/Country renderers compute coordinates and wrap these in `<g>`.
 */
import type { SVGProps } from 'react'
import type { Severity } from '@/types/map'

export function severityColour(severity: Severity): string {
  return `var(--severity-${severity})`
}

export interface IncidentDotProps extends Omit<SVGProps<SVGGElement>, 'opacity'> {
  cx: number
  cy: number
  severity: Severity
  /** Base dot radius in px (halo = 3× this). Defaults to 4 per spec (8px dot). */
  radius?: number
}

/** 8px filled dot + 25% halo — the canonical map marker body. */
export function IncidentDot({
  cx,
  cy,
  severity,
  radius = 4,
  ...rest
}: IncidentDotProps) {
  const fill = severityColour(severity)
  const haloR = radius * 3
  return (
    <g {...rest}>
      <circle cx={cx} cy={cy} r={haloR} fill={fill} fillOpacity={0.25} />
      <circle cx={cx} cy={cy} r={radius} fill={fill} />
    </g>
  )
}

export interface IncidentPulseRingProps extends SVGProps<SVGCircleElement> {
  cx: number
  cy: number
  severity: Severity
  /** Inner radius at t=0, in px. Ring animates out to `toRadius`. */
  fromRadius?: number
  /** Outer radius at the end of the 1.5s animation, in px. */
  toRadius?: number
}

/**
 * One-shot pulse ring. Uses SMIL `<animate>` with `repeatCount={1}` +
 * `fill="freeze"` so the ring finishes in the faded-out state and doesn't
 * loop. Consumers gate rendering on `isNew` so old incidents don't re-fire
 * the animation on re-render.
 */
export function IncidentPulseRing({
  cx,
  cy,
  severity,
  fromRadius = 4,
  toRadius = 24,
  ...rest
}: IncidentPulseRingProps) {
  const stroke = severityColour(severity)
  return (
    <circle
      data-testid="incident-pulse-ring"
      cx={cx}
      cy={cy}
      r={fromRadius}
      fill="none"
      stroke={stroke}
      strokeWidth={1.5}
      {...rest}
    >
      <animate
        attributeName="r"
        from={fromRadius}
        to={toRadius}
        dur="1.5s"
        fill="freeze"
        repeatCount={1}
      />
      <animate
        attributeName="opacity"
        from={0.9}
        to={0}
        dur="1.5s"
        fill="freeze"
        repeatCount={1}
      />
    </circle>
  )
}

export interface UnattribBurstProps extends SVGProps<SVGGElement> {
  cx: number
  cy: number
  severity: Severity
  /** Burst radius in px. */
  radius?: number
}

/**
 * Radial-burst glyph for unattributed attacks. No origin point, no attack
 * line — just the burst at the target plus a small UNATTRIB label above.
 */
export function UnattribBurst({
  cx,
  cy,
  severity,
  radius = 18,
  ...rest
}: UnattribBurstProps) {
  const fill = severityColour(severity)
  return (
    <g data-testid="unattrib-burst" {...rest}>
      <circle cx={cx} cy={cy} r={radius} fill={fill} fillOpacity={0.12} />
      <circle cx={cx} cy={cy} r={radius * 0.6} fill={fill} fillOpacity={0.2} />
      <text
        x={cx}
        y={cy - radius - 4}
        textAnchor="middle"
        fontSize={9}
        fill="var(--text-tertiary)"
        className="font-mono"
      >
        UNATTRIB
      </text>
    </g>
  )
}

export interface AttackArcProps extends SVGProps<SVGPathElement> {
  src: [number, number]
  dst: [number, number]
  severity: Severity
  /** Whether to render the 300ms trail-reveal stroke-dashoffset animation. */
  isNew?: boolean
}

/**
 * Dashed attack-path arc between attacker origin and target. The arc rises
 * above the midpoint of src→dst so it reads as a trajectory rather than a
 * straight line. When `isNew` is set, the dasharray animates in over 300ms.
 */
export function AttackArc({
  src,
  dst,
  severity,
  isNew = false,
  ...rest
}: AttackArcProps) {
  const stroke = severityColour(severity)
  const midX = (src[0] + dst[0]) / 2
  const midY = Math.min(src[1], dst[1]) - 80
  const d = `M${src[0]},${src[1]} Q${midX},${midY} ${dst[0]},${dst[1]}`
  return (
    <path
      data-testid="attack-arc"
      d={d}
      stroke={stroke}
      strokeWidth={1.5}
      strokeDasharray="4 3"
      fill="none"
      // DESIGN.md §Geographic Map: flow arcs at --severity-* 40% opacity.
      opacity={0.4}
      {...rest}
    >
      {isNew && (
        <animate
          attributeName="stroke-dashoffset"
          from={200}
          to={0}
          dur="0.3s"
          fill="freeze"
          repeatCount={1}
        />
      )}
    </path>
  )
}
